import type { ReactElement, ReactNode } from "react";
import { SequenceTimeContext, useCollector } from "./context.js";
import type {
  DissolveProps,
  FadeThroughProps,
  HFCompositionProps,
  SequenceProps,
  SlideProps,
  TypeMatchCutProps,
  VariableDefinition,
  WhipPanCutProps,
  ZoomThroughTransitionProps,
} from "./types.js";

function serializeVariables(variables: VariableDefinition[] | undefined): string | undefined {
  return variables?.length ? JSON.stringify(variables) : undefined;
}

export function HFComposition({
  id,
  duration,
  width,
  height,
  fps = 30,
  variables,
  children,
}: HFCompositionProps): ReactElement {
  const collector = useCollector();
  collector.registerComposition({ id, duration, width, height, fps, variables });

  return (
    <div
      id="root"
      data-composition-id={id}
      data-start="0"
      data-duration={duration}
      data-width={width}
      data-height={height}
      data-fps={fps}
      data-composition-variables={serializeVariables(variables)}
    >
      {children}
    </div>
  );
}

export function Sequence({
  id,
  start,
  duration,
  track,
  className,
  style,
  children,
}: SequenceProps): ReactElement {
  const collector = useCollector();
  collector.registerSequence({ id, start, duration, track });

  return (
    <SequenceTimeContext.Provider value={start}>
      <div
        id={id}
        data-hf-id={id}
        className={["clip", "hf-sequence", className].filter(Boolean).join(" ")}
        data-start={start}
        data-duration={duration}
        data-track-index={track}
        style={{ zIndex: track, ...style }}
      >
        <div className="hf-sequence-visual">{children}</div>
      </div>
    </SequenceTimeContext.Provider>
  );
}

export function Dissolve({ from, to, at, duration, ease = "power2.inOut" }: DissolveProps): null {
  const collector = useCollector();
  collector.registerDissolve({ from, to, at, duration, ease });
  const tl = collector.timeline();
  tl.to(`#${from} > .hf-sequence-visual`, { opacity: 0, duration, ease }, at);
  tl.fromTo(
    `#${to} > .hf-sequence-visual`,
    { opacity: 0 },
    { opacity: 1, duration, ease },
    at,
  );
  return null;
}

export function Slide({
  from,
  to,
  at,
  duration,
  ease = "power3.inOut",
  direction = "left",
}: SlideProps): null {
  const collector = useCollector();
  collector.registerSlide({ from, to, at, duration, ease, direction });
  const tl = collector.timeline();
  const vertical = direction === "up" || direction === "down";
  const axis = vertical ? "y" : "x";
  const outgoing = direction === "left" || direction === "up" ? "-100%" : "100%";
  const incoming = direction === "left" || direction === "up" ? "100%" : "-100%";

  tl.fromTo(
    `#${from} > .hf-sequence-visual`,
    { [axis]: "0%", opacity: 1 },
    { [axis]: outgoing, opacity: 1, duration, ease },
    at,
  );
  tl.fromTo(
    `#${to} > .hf-sequence-visual`,
    { [axis]: incoming, opacity: 1 },
    { [axis]: "0%", opacity: 1, duration, ease },
    at,
  );
  tl.set(`#${from} > .hf-sequence-visual`, { opacity: 0 }, at + duration);
  return null;
}

export function FadeThrough({
  id,
  from,
  to,
  at,
  duration,
  ease = "power2.inOut",
  wash = "paper",
  strength = 1,
  shape = "flat",
}: FadeThroughProps): ReactElement {
  const collector = useCollector();
  collector.registerFadeThrough({ id, from, to, at, duration, ease, wash, strength, shape });
  const tl = collector.timeline();
  const washIn = duration * (0.35 / 0.9);
  const outgoing = duration * 0.5;
  const incoming = duration * (0.55 / 0.9);
  const washOut = duration * 0.5;
  tl.to(`#${from} > .hf-sequence-visual`, { opacity: 0, duration: outgoing, ease }, at);
  tl.to(`#${id}`, { opacity: strength, duration: washIn, ease }, at);
  tl.to(`#${id}`, { opacity: 0, duration: washOut, ease }, at + washIn);
  tl.fromTo(
    `#${to} > .hf-sequence-visual`,
    { opacity: 0 },
    { opacity: 1, duration: incoming, ease: "power2.out" },
    at + washIn,
  );

  const washes: Record<string, string> = {
    paper: "#f8f5ec",
    white: "#ffffff",
    ink: "#0b0b0f",
    accent: "var(--accent, #38bdf8)",
  };
  const color = washes[wash] ?? wash;
  const background =
    shape === "bloom"
      ? `radial-gradient(circle at 50% 50%, ${color} 0%, rgba(248,245,236,0) 72%)`
      : color;

  return <div id={id} className="hf-fade-through-wash" style={{ background }} aria-hidden="true" />;
}

/** Descriptive alias retained alongside the shorter historical name. */
export const FadeThroughColor = FadeThrough;

function splitHeadline(text: string): [string, string] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return ["Meet the", "new editor"];
  const at = Math.ceil(words.length / 2);
  return [words.slice(0, at).join(" "), words.slice(at).join(" ") || words[at - 1]!];
}

export function TypeMatchCut({ id, from, to, at, duration, text = "Meet the new editor", accent = "green" }: TypeMatchCutProps): ReactElement {
  const collector = useCollector();
  const definition = { id, from, to, at, duration, text, accent };
  collector.registerTypeMatchCut(definition);
  const tl = collector.timeline();
  const scale = duration < 2.2 ? duration / 2.2 : 1;
  const intro = 1.05 * scale;
  const outro = 1.15 * scale;
  const hold = Math.max(0, duration - intro - outro);
  const outAt = at + intro + hold;
  const splitAt = at + 0.14 * scale;
  const splitDuration = 0.58 * scale;
  const panelAt = at + 0.58 * scale;
  const panelDuration = 0.47 * scale;
  const arcFirst = outro * 0.42;
  tl.set(`#${id}`, { autoAlpha: 1 }, at);
  tl.set(`#${from} > .hf-sequence-visual`, { opacity: 0 }, at);
  tl.fromTo(`#${to} > .hf-sequence-visual`, { opacity: 1, scaleX: 0.02, scaleY: 0.02, borderRadius: "12cqw" }, { opacity: 1, scaleX: 0.46, scaleY: 0.14, borderRadius: "6cqw", duration: panelDuration, ease: "power3.out" }, panelAt);
  tl.to(`#${id} .hf-tmc-top`, { y: "-20cqh", duration: splitDuration, ease: "power3.out" }, splitAt);
  tl.to(`#${id} .hf-tmc-bottom`, { y: "20cqh", duration: splitDuration, ease: "power3.out" }, splitAt);
  if (hold > 0) {
    tl.to(`#${to} > .hf-sequence-visual`, { scaleX: 0.47, scaleY: 0.145, duration: hold / 2, ease: "sine.inOut", yoyo: true, repeat: 1 }, at + intro);
  }
  tl.to(`#${to} > .hf-sequence-visual`, { scaleX: 1, scaleY: 1, borderRadius: 0, duration: outro, ease: "power4.out" }, outAt);
  tl.to(`#${id} .hf-tmc-top`, { x: "3cqw", y: "-42cqh", duration: arcFirst, ease: "power2.in" }, outAt);
  tl.to(`#${id} .hf-tmc-top`, { x: "-5cqw", y: "-125cqh", duration: outro - arcFirst, ease: "power2.in" }, outAt + arcFirst);
  tl.to(`#${id} .hf-tmc-bottom`, { x: "-3cqw", y: "42cqh", duration: arcFirst, ease: "power2.in" }, outAt);
  tl.to(`#${id} .hf-tmc-bottom`, { x: "5cqw", y: "125cqh", duration: outro - arcFirst, ease: "power2.in" }, outAt + arcFirst);
  tl.set(`#${id}`, { autoAlpha: 0 }, at + duration);
  const [top, bottom] = splitHeadline(text);
  const fontSize = `${Math.min(10.5, 40 / Math.max(1, top.length, bottom.length)).toFixed(2)}cqw`;
  const colors = { green: "#34d399", blue: "#60a5fa", violet: "#a78bfa" };
  return <div id={id} className="hf-type-match-cut" data-hf-transition="type-match-cut" data-layout-allow-overflow data-layout-allow-overlap data-layout-allow-occlusion style={{ "--hf-tmc-accent": colors[accent], "--hf-tmc-size": fontSize } as React.CSSProperties} aria-label={text}><style>{`#${id}{position:absolute;inset:0;z-index:901;overflow:hidden;container-type:size;opacity:0;pointer-events:none;color:var(--fg,#f7f8fa);font-family:var(--font-display,Inter,system-ui,sans-serif)}#${id} .hf-tmc-half{position:absolute;top:calc(50% - .44em);display:block;width:40cqw;font-size:var(--hf-tmc-size,min(10.5cqw,18cqh));font-weight:900;line-height:.88;letter-spacing:-.045em;white-space:nowrap;text-shadow:0 1.4cqh 4.8cqh rgba(0,0,0,.72);will-change:transform}#${id} .hf-tmc-top{right:52cqw;text-align:right}#${id} .hf-tmc-bottom{left:52cqw;text-align:left}`}</style><span className="hf-tmc-half hf-tmc-top">{top}</span><span className="hf-tmc-half hf-tmc-bottom">{bottom}</span></div>;
}

export function WhipPanCut({ id, from, to, at, duration, direction = "left", accent = "green", lead = 0.25 }: WhipPanCutProps): ReactElement {
  const collector = useCollector();
  const definition = { id, from, to, at, duration, direction, accent, lead };
  collector.registerWhipPanCut(definition);
  const tl = collector.timeline();
  const baseWhip = 0.55;
  const phaseScale = duration < lead + baseWhip ? duration / (lead + baseWhip) : 1;
  const whipAt = at + lead * phaseScale;
  const whipDuration = baseWhip * phaseScale;
  const sign = direction === "left" ? -1 : 1;
  tl.set(`#${id}`, { autoAlpha: 1 }, whipAt);
  tl.fromTo(`#${from} > .hf-sequence-visual`, { x: "0%", opacity: 1 }, { x: `${sign * 100}%`, opacity: 1, duration: whipDuration, ease: "power3.inOut" }, whipAt);
  tl.fromTo(`#${to} > .hf-sequence-visual`, { x: `${-sign * 100}%`, opacity: 1 }, { x: "0%", opacity: 1, duration: whipDuration, ease: "power3.inOut" }, whipAt);
  tl.fromTo(`#${from} > .hf-sequence-visual,#${to} > .hf-sequence-visual`, { "--hf-whip-blur": "0px" }, { "--hf-whip-blur": "16px", duration: whipDuration / 2, ease: "power3.in" }, whipAt);
  tl.to(`#${from} > .hf-sequence-visual,#${to} > .hf-sequence-visual`, { "--hf-whip-blur": "0px", duration: whipDuration / 2, ease: "power3.out" }, whipAt + whipDuration / 2);
  tl.fromTo(`#${id}`, { x: `${-sign * 50}cqw`, autoAlpha: 0 }, { x: `${sign * 50}cqw`, autoAlpha: 1, duration: whipDuration / 2, ease: "power3.in" }, whipAt);
  tl.to(`#${id}`, { autoAlpha: 0, duration: whipDuration / 2, ease: "power3.out" }, whipAt + whipDuration / 2);
  tl.set(`#${from} > .hf-sequence-visual`, { opacity: 0 }, whipAt + whipDuration);
  const colors = { green: "var(--brand,#22c55e)", blue: "var(--accent,#38bdf8)", violet: "var(--accent-2,#c5a3ff)" };
  return <div id={id} className="hf-whip-pan-seam" data-hf-transition="whip-pan-cut" data-layout-allow-overflow style={{ position: "absolute", zIndex: 902, top: 0, bottom: 0, left: "50%", width: "7px", opacity: 0, background: colors[accent], boxShadow: `0 0 34px ${colors[accent]}`, pointerEvents: "none" }} aria-hidden="true"/>;
}

export function ZoomThroughTransition({ id, from, to, at, duration, depth = "standard", accent = "blue" }: ZoomThroughTransitionProps): ReactElement {
  const collector = useCollector();
  const definition = { id, from, to, at, duration, depth, accent };
  collector.registerZoomThroughTransition(definition);
  const tl = collector.timeline();
  const push = Math.min(0.9, duration);
  const start = at + Math.max(0, Math.min(0.35, duration - push));
  const depths = { close: 0.55, standard: 1, far: 1.7 };
  const factor = depths[depth];
  tl.fromTo(`#${to} > .hf-sequence-visual`, { opacity: 1, "--hf-zoom-clip-right": "100%", x: 60 * factor, scale: 1.04 }, { opacity: 1, "--hf-zoom-clip-right": "0%", x: 0, scale: 1, duration: push, ease: "power3.inOut" }, start);
  tl.fromTo(`#${from} > .hf-sequence-visual`, { x: 0, scale: 1, opacity: 1 }, { x: -70 * factor, scale: 0.96, opacity: 1, duration: push, ease: "power3.inOut" }, start);
  tl.fromTo(`#${id} .hf-zoom-flash`, { "--hf-fx-x": "-110%" }, { "--hf-fx-x": "110%", duration: Math.min(0.58, push), ease: "power2.inOut" }, start + Math.min(0.18, push * 0.2));
  tl.fromTo(`#${id} .hf-zoom-rgb`, { opacity: 0, x: -16 }, { opacity: 1, x: 16, repeat: 3, yoyo: true, duration: Math.min(0.07, push / 8), ease: "steps(1)" }, start + Math.min(0.32, push * 0.36));
  tl.set(`#${id}`, { autoAlpha: 0 }, start + push);
  const colors = { blue: "var(--accent,#38bdf8)", green: "var(--brand,#22c55e)", violet: "var(--accent-2,#c5a3ff)" };
  return <div id={id} className="hf-zoom-through-fx" data-hf-transition="zoom-through-transition" data-layout-allow-overflow data-layout-allow-occlusion style={{ position: "absolute", inset: 0, zIndex: 903, overflow: "hidden", opacity: 1, pointerEvents: "none", "--hf-zoom-accent": colors[accent] } as React.CSSProperties} aria-hidden="true"><div className="hf-zoom-flash" data-layout-ignore style={{position:"absolute",inset:0,transform:"translateX(var(--hf-fx-x,-110%))",background:"linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.35),rgba(255,255,255,0))",mixBlendMode:"screen"}}/><div className="hf-zoom-rgb" data-layout-ignore style={{position:"absolute",inset:0,opacity:0,background:"linear-gradient(90deg,rgba(239,68,68,.35),rgba(255,255,255,0),rgba(34,211,238,.35))"}}/></div>;
}

export function TransitionSeries({ children }: { children?: ReactNode }): ReactElement {
  return <>{children}</>;
}

TransitionSeries.Sequence = Sequence;
TransitionSeries.Dissolve = Dissolve;
TransitionSeries.Slide = Slide;
TransitionSeries.FadeThrough = FadeThrough;
TransitionSeries.FadeThroughColor = FadeThroughColor;
TransitionSeries.TypeMatchCut = TypeMatchCut;
TransitionSeries.WhipPanCut = WhipPanCut;
TransitionSeries.ZoomThrough = ZoomThroughTransition;
